import NotificationIcon from '@/assets/icons/notification-icon.svg?react';
import { Badge, Box, IconButton, Popover, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect } from 'react';
import NotificationList from '@/components/global/menu/NotificationList.jsx';
import { useNotificationCount } from '@/composables/useNotificationCount.js';
import { useRefreshNotificationCount } from '@/composables/useRefreshNotificationCount.js';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { useTranslate } from 'react-admin';
import { useScrollLock } from '@/composables/useScrollLock';

const NotificationButton = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const isOpen = Boolean(anchorEl);
    const refreshNotificationCount = useRefreshNotificationCount();

    const handleToggle = event => {
        if (isOpen) {
            setAnchorEl(null);
        } else {
            refreshNotificationCount();
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton className={`notification-icon ${isOpen ? 'active' : ''}`} onClick={handleToggle}>
                <NotificationBadge />
            </IconButton>
            <NotificationPopover anchorEl={anchorEl} isOpen={isOpen} onClose={handleClose} />
        </>
    );
};

const StyledPopover = styled(Popover)(({ theme }) => ({
    '&.notification-popover .MuiPaper-root': {
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100vw',
            left: '0 !important',
            top: '57px !important',
            transform: 'none !important',
            transformOrigin: 'none !important',
            borderRadius: '0px',
        },
        '& .notification-popover-content': {
            padding: 24,
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 130px)',
            borderRadius: '16px',
            [theme.breakpoints.down('sm')]: {
                maxHeight: 'calc(100vh - 107px)',
                boxShadow: 'none',
                minWidth: 'calc(100vw - 48px)',
            },
            '& h6': {
                lineHeight: '24px',
                marginBottom: '16px',
                minWidth: '492px',
                [theme.breakpoints.down('sm')]: {
                    minWidth: '100%',
                    fontSize: '18px',
                    lineHeight: '24px',
                },
            },
        },
    },
}));

const NotificationBadge = () => {
    const notificationCount = useNotificationCount();
    const theme = useTheme();
    return (
        <Badge
            badgeContent={notificationCount > 0 ? notificationCount : null}
            color='primary'
            sx={{ [theme.breakpoints.down('md')]: { display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
        >
            <NotificationIcon />
        </Badge>
    );
};

const NotificationPopover = ({ anchorEl, isOpen, onClose }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [shouldLockScroll, setShouldLockScroll] = useState(false);

    useEffect(() => {
        if (isMobile && isOpen) {
            const timeout = setTimeout(() => {
                setShouldLockScroll(true);
            }, 100);
            return () => clearTimeout(timeout);
        } else {
            setShouldLockScroll(false);
        }
    }, [isMobile, isOpen]);

    useScrollLock(shouldLockScroll);

    return (
        <StyledPopover
            anchorEl={anchorEl}
            open={isOpen}
            onClose={onClose}
            disableScrollLock={!isMobile}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: isMobile ? 'center' : 'right',
            }}
            className='notification-popover'
        >
            <Box className='notification-popover-content'>
                <Typography variant='h6'>{translate('notification.eventsLast30Days')}</Typography>
                <NotificationList />
            </Box>
        </StyledPopover>
    );
};

export default NotificationButton;

NotificationPopover.propTypes = {
    anchorEl: PropTypes.element,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
};
